import { derived, get } from 'svelte/store';
import { toast } from '@Shelly/shelly-design-system';
import { format } from 'src/services/i18n';
import { postReq } from 'src/services/http';
import { user_data, user_settings } from 'src/stores/account/user';

/**
 * Derived store: true when the currently logged-in account has an email that is
 * not yet verified (FE-1468). Edge cases:
 * - Accounts with no email at all → false (no indicator shown).
 * - Units → reflects the logged-in session's `mail_validated` flag.
 * - Code requested but not yet confirmed → remains true until `mail_validated`
 *   flips to 1 via the `/user/check_code` success path.
 */
export const isEmailUnverified = derived(
	[user_settings, user_data],
	([$user_settings, $user_data]) => Boolean($user_data?.email) && $user_settings?.mail_validated === 0,
);

/**
 * Request a new email verification code for the logged-in account.
 * Shared by `ConfirmEmail.svelte` and the inline popover in User Settings.
 *
 * Returns `true` on success so callers can reveal the "enter code" input.
 */
export const requestEmailValidationCode = async (): Promise<boolean> => {
	const _format = get(format);
	const res = await postReq('/user/send_validation', {});

	if (res?.isok) {
		toast.open({ type: 'success', title: _format('validation_sent_toast') });
		return true;
	}

	if (res?.errors) {
		toast.open({
			type: 'danger',
			title: _format(Object.keys(res.errors)[0]),
		});
		return false;
	}

	toast.open({ type: 'danger', title: _format('error_global') });
	return false;
};

/**
 * Submit a verification code, mark the email as validated on success, and
 * surface the appropriate toast. Keeps parity with `ConfirmEmail.svelte`'s
 * inline flow so the popover-based entry point (FE-1468) behaves identically.
 *
 * Returns `true` on successful validation so the caller can close the popover.
 */
export const confirmEmailValidationCode = async (
	code: string,
	successToastMessage?: string,
): Promise<boolean> => {
	const _format = get(format);
	const data = await postReq('/user/check_code', { code });

	if (data?.isok) {
		const res = await postReq('/user/set_mail_notifications_permissions', {
			permissions: 1,
		});

		if (res?.isok) {
			user_settings.update((current) => {
				current.mail_notifications_permissions = 1;
				current.mail_validated = 1;
				return current;
			});
			toast.open({
				type: 'success',
				title: successToastMessage || _format('validation_active_toast'),
			});
			return true;
		}

		toast.open({ type: 'danger', title: _format('setting_err_toast') });
		return false;
	}

	if (data?.errors) {
		toast.open({
			type: 'danger',
			title: _format(Object.keys(data.errors)[0]),
		});
	}

	return false;
};
